import { CloudUpload } from '@material-ui/icons';
import React, { ChangeEvent, useRef } from 'react';
import { makeStyles } from '@material-ui/core';
import { Colors } from '../../constants/colors';
import { fileFormats } from '../../constants/files';
import { FileItem } from '../FileItem';
import { OutlinedButton } from '../buttons/OutlinedButton';
import { AlignText } from '../helpers/AlignText';
import { UserDocument } from '../../model/UserDocument';

import { useSelector } from 'react-redux';
import { selectUser } from '../../store/auth/auth.selector';
import { RequestType } from '../../model/enums/RequestType';

type FileUploadProps = {
  documents: UserDocument[],
  onDocumentDelete: (document: UserDocument) => void,
  onCategoryChange: (document: UserDocument, category: string) => void
  onFileUpload: (files: File[]) => void
}

const useStyles = makeStyles({
  hidden: {
    display: 'none'
  },
  uploadButton: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginTop: 30
  },
  formats: {
    fontSize: 14,
    color: Colors.grey,
    marginTop: 30
  },
  note: {
    fontSize: 14,
    lineHeight: 1.8,
    marginTop: 30,

    '& strong': {
      color: Colors.primary
    },
    '& ul': {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      margin: '0 30px',
      padding: 0,
      listStyle: 'none'
    },
    '& li::before': {
      content: '\'\u2022\'',
      color: Colors.primary,
      fontWeight: 700,
      display: 'inline-block',
      width: '1em',
      marginLeft: '-1em'
    }
  }
});

export const FileUpload = (props: FileUploadProps) => {

  const user = useSelector(selectUser);
  const classes = useStyles();
  const hiddenFileInput = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (e: ChangeEvent<HTMLInputElement>) => {
    const { files } = e.target as HTMLInputElement;

    // Create array of files, so it is preserved when the input is cleared
    const uploadedFiles: File[] = [];
    for (const i in files) {
      if (files.hasOwnProperty(i)) {
        uploadedFiles.push(files[Number(i)]);
      }
    }

    // Clear the input
    if (hiddenFileInput?.current) {
      hiddenFileInput.current.value = '';
    }

    if (!uploadedFiles.length) {
      return;
    }

    // Upload files
    await props.onFileUpload(uploadedFiles);
  };

  const handleButtonClick = () => {
    if (hiddenFileInput && hiddenFileInput.current) {
      hiddenFileInput.current.click();
    }
  };

  return (
    <>
      {props.documents.map((document, key) =>
        <FileItem key={key}
                  document={document}
                  onDelete={() => props.onDocumentDelete(document)}
                  onCategoryChange={(category) => props.onCategoryChange(document, category)} />
      )}

      <div className={classes.uploadButton}>
        <OutlinedButton onClick={handleButtonClick}>
          Vybrat soubory
          <CloudUpload />
        </OutlinedButton>

        <AlignText to='center'>
          <input type='file' ref={hiddenFileInput}
                 onChange={handleFileSelect}
                 className={classes.hidden}
                 multiple={true}
                 accept={fileFormats.join(', ')} />

          <div className={classes.formats}>
            Podporované formáty: {fileFormats.join(', ')}. Maximální velikost souboru je 25MB.
          </div>

          <div className={classes.note}>
            Nahrávejte, prosím, pouze <strong>relevantní podklady</strong>, např.:
            <ul>
              <li>Lékařské zprávy</li>
              <li>Výsledky laboratorních vyšetření</li>
              <li>
                {(() => {
                  if (user?.requestType === RequestType.DoctorRegistration) {
                    return 'RTG, CT, MR atd. je možné zaslat do ePACS IKEM';
                  } else {
                    return 'O nahrání rentgenových snímků, CT, MRI atd. do systému ePACS IKEM můžete požádat tam, kde daný dokument vznikl';
                  }
                })()}
              </li>
            </ul>
          </div>
        </AlignText>
      </div>
    </>
  );
};
